import i18n from '@/i18n/config'

import { ColumnTypes, ITableColumn } from '@makyoapp/makyo-sdk/models/Table'
import {
  ALL_FUNCTIONS_MAP,
  ColumnAdapter,
  FunctionColOptionsOnly,
  FunctionIfsThenExpression,
  FunctionItemDefaultValue,
} from '@makyoapp/makyo-common'

import { Cell } from '@/core/models/columns'
import { NumberCell } from '@/core/models/columns/number'

import { FunctionSettings } from './types'

const DEFERRED_COLUM_TYPES = [
  ColumnTypes.Connection,
  ColumnTypes.Lookup,
  ColumnTypes.Function,
  ColumnTypes.Join,
  ColumnTypes.Math,
  ColumnTypes.Template,
  ColumnTypes.SingleCell,
]

export function getFnColumnSettings(column: ITableColumn | ColumnAdapter): FunctionSettings | undefined {
  // @ts-expect-error
  let colSettings = (column.settings ?? column._Settings) as Record<string, any>

  if ('functionValue' in colSettings) {
    return colSettings.functionValue as FunctionSettings
  }

  if ('functionOptions' in colSettings) {
    let fnOptions = colSettings.functionOptions as FunctionColOptionsOnly
    if ('functionValue' in fnOptions) {
      return fnOptions.functionValue as FunctionSettings
    }
  }
}

export function filterFnReturnTypes(
  column: ITableColumn | ColumnAdapter,
  types: ColumnTypes[],
  includeUnknown: boolean = false
): boolean {
  const fnSettings = getFnColumnSettings(column)
  if (!fnSettings) return false
  const fnItem = ALL_FUNCTIONS_MAP[fnSettings.item.id]
  if (!fnItem) return false

  let isIncluded = !!fnItem.returnType && types.includes(fnItem.returnType)
  if (includeUnknown) {
    isIncluded = isIncluded || !!fnItem.returnUnknown
  }
  return isIncluded
}

export function getFnReturnType(column: ITableColumn | ColumnAdapter): ColumnTypes | undefined {
  let fnSettings = getFnColumnSettings(column)
  if (!fnSettings) return
  const fnItem = ALL_FUNCTIONS_MAP[fnSettings.item.id]
  return fnItem.returnUnknown ? undefined : (fnItem.returnType as ColumnTypes)
}

function injectDeps(deps: Set<string>, arg: FunctionItemDefaultValue, columnsMap: Map<string, ITableColumn>): void {
  if (typeof arg === 'object' && arg !== null) {
    // Data type is FunctionColumnValue
    if ('isCustom' in arg) {
      if (arg.isCustom === false) {
        if (arg.value) {
          columnsMap.forEach((c) => {
            if (c._id === arg.value && DEFERRED_COLUM_TYPES.includes(c._Type)) {
              deps.add(c._id)
            }
          })
        }
      }
    } else if ('conditions' in arg) {
      // Data type value is FunctionIfsValue | FunctionIfsThenValue
      arg.conditions.forEach((cond) => {
        columnsMap.forEach((c) => {
          if (DEFERRED_COLUM_TYPES.includes(c._Type)) {
            if (
              c._id === cond.rhs?.value ||
              c._id === cond.rhsEnd?.value ||
              c._id === (cond as FunctionIfsThenExpression)?.then?.value
            ) {
              deps.add(c._id)
            }
          }
        })
      })
    } else if ('condition' in arg) {
      // Data type value is `FunctionIfValue`
      columnsMap.forEach((c) => {
        if (DEFERRED_COLUM_TYPES.includes(c._Type)) {
          if (
            c._id === arg.condition?.rhs?.value ||
            c._id === arg.condition?.rhsEnd?.value ||
            // it could be then also just for safety we also check it here
            // even though there is currently no single then only without inside array
            c._id === (arg.condition as FunctionIfsThenExpression)?.then?.value
          ) {
            deps.add(c._id)
          }
        }
      })
    } else if ('statements' in arg) {
      // Data type is `FunctionIfsColumnMultiValue`
      arg.statements.forEach((stt) => {
        const columns = stt.columns.filter((c) => c.isCustom === false).map((c) => c.value)

        stt.conditions.forEach((c) => {
          if (c.rhs?.isCustom === false) {
            columns.push(c.rhs.value)
          }
          if (c.rhsEnd?.isCustom === false) {
            columns.push(c.rhsEnd.value)
          }
        })

        columnsMap.forEach((c) => {
          if (columns.includes(c._id)) {
            deps.add(c._id)
          }
        })
      })
    }
  }
}

export function generateFnNodeDeps(col: ITableColumn, columnsMap: Map<string, ITableColumn>): string[] {
  // Find if the function has any dependencies to other funciton columns.
  const fnSettings = getFnColumnSettings(col)
  if (!fnSettings) {
    return []
  }

  const deps: Set<string> = new Set()

  const fnValue = fnSettings.value

  if (fnValue) {
    // We loop trough the arguments of the function to find the dependencies.
    Object.values(fnValue).forEach((arg) => {
      if (Array.isArray(arg)) {
        arg.forEach((a) => {
          injectDeps(deps, a, columnsMap)
        })
      } else {
        injectDeps(deps, arg, columnsMap)
      }
    })
  }

  return Array.from(deps)
}

export function getMaxFieldsLength(fnSettings: FunctionSettings): number {
  const value = fnSettings.value
  let maxLength = 0

  for (const key in value) {
    const item = value[key]
    if (Array.isArray(item)) {
      maxLength = Math.max(maxLength, item.length)
    }
  }

  return maxLength
}

export function formatRelativeFxResult(result: any) {
  if (!result) return null
  const regex = result.match(/([+-])(\d+)\s+(\w+)/)
  if (!regex) return null

  const [sign, value, unit] = regex.slice(1)

  let signTxt = sign === '+' ? 'plus' : 'minus'
  let unitTxt = unit
  let unitPlural = value === '1' ? 'one' : 'other'

  return i18n.t(`time_relative.${unitTxt}.${signTxt}_${unitPlural}`, { count: value })
}

export function generateDeferredNodeDeps(colIds: string[], columnsMap: Map<string, ITableColumn>): string[] {
  const deps: Set<string> = new Set()

  colIds.forEach((id) => {
    const col = columnsMap.get(id)
    if (col && DEFERRED_COLUM_TYPES.includes(col._Type)) {
      deps.add(col._id)
    }
  })

  return Array.from(deps)
}

export function mathScopeEnsureNumber(cellValue: Cell | Cell[]): number {
  switch (true) {
    case cellValue instanceof NumberCell: {
      return cellValue.value as number
    }
    case Array.isArray(cellValue): {
      return typeof cellValue[0] === 'number' ? Number(cellValue[0]) : 0
    }
    case typeof cellValue === 'string': {
      return Number(cellValue)
    }
    default:
      return typeof cellValue === 'number' && !isNaN(cellValue) ? cellValue : 0
  }
}

export function getSingleCellRowIndex(rowOptions: string, row: string, tableRowsLength: number): number {
  let rowIdx = 0

  switch (rowOptions) {
    case 'first': {
      rowIdx = 0
      break
    }
    case 'last': {
      rowIdx = tableRowsLength - 1
      break
    }
    case 'from-start': {
      rowIdx = Number(row) - 1
      break
    }
    case 'from-end': {
      rowIdx = tableRowsLength - Number(row) - 1
      break
    }
    case 'random': {
      rowIdx = Math.floor(Math.random() * tableRowsLength)
      break
    }

    default: {
      break
    }
  }

  if (rowIdx < 0 || rowIdx >= tableRowsLength) {
    return 0
  }

  return rowIdx
}
